import { useState, useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useClientChats, useChatMessages, useSendMessage } from '@/hooks/useClientChat';
import { queryKeys } from '@/lib/query-keys';
import type { ChatMessage, ChatResponse } from '@/types/chat';

/**
 * A unified hook for managing chat interactions, supporting both client-scoped
 * and org-wide (global) conversations.
 *
 * @param clientId - Client UUID for client-scoped chats, null for global chats.
 *   Pass undefined to disable the hook entirely (queries will not run).
 * @param scope - Explicit scope override. Defaults to 'client' when clientId
 *   is provided, 'global' otherwise.
 */
export function useChatWithContext(clientId?: string | null, scope?: 'client' | 'global') {
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [pendingFirstMessage, setPendingFirstMessage] = useState<ChatMessage | null>(null);
  const queryClient = useQueryClient();

  const effectiveScope: 'client' | 'global' = scope ?? (clientId ? 'client' : 'global');

  // Reset active chat when context changes (e.g. navigating between pages)
  // to prevent sending messages to a chat that belongs to a different scope/client
  const prevContextRef = useRef({ clientId, effectiveScope });
  useEffect(() => {
    const prev = prevContextRef.current;
    if (prev.clientId !== clientId || prev.effectiveScope !== effectiveScope) {
      setActiveChatId(null);
      setPendingFirstMessage(null);
      prevContextRef.current = { clientId, effectiveScope };
    }
  }, [clientId, effectiveScope]);

  const chats = useClientChats(clientId ?? null);
  const messages = useChatMessages(activeChatId ?? undefined);
  const sendMessageMutation = useSendMessage();

  /**
   * Send a message with optimistic UI updates.
   * Returns the ChatResponse on success, throws on failure.
   */
  const handleSendMessage = useCallback(
    async (content: string): Promise<ChatResponse | undefined> => {
      if (!content.trim()) return;

      const tempUserId = 'temp-user';
      const tempMessageId = `temp-${Date.now()}`;

      // Create optimistic user message
      const optimisticUserMessage: ChatMessage = {
        id: tempMessageId,
        chat_id: activeChatId ?? '',
        role: 'user',
        content,
        metadata: null,
        context_sources: null,
        created_by: tempUserId,
        created_at: new Date().toISOString(),
      };

      if (activeChatId) {
        // Existing chat: update the query cache directly
        queryClient.setQueryData(
          queryKeys.chat.messages(activeChatId),
          (old: ChatMessage[] | undefined) => {
            return old ? [...old, optimisticUserMessage] : [optimisticUserMessage];
          },
        );
      } else {
        // First message (no chat yet): track separately so UI can display it
        setPendingFirstMessage(optimisticUserMessage);
      }

      try {
        const response = await sendMessageMutation.mutateAsync({
          clientId: clientId ?? undefined,
          message: content,
          chatId: activeChatId ?? undefined,
          scope: effectiveScope,
        });

        // If a new chat was created, seed the cache before switching
        if (!activeChatId && response.chatId) {
          // Build the assistant reply from the response
          const assistantMessage: ChatMessage = {
            id: response.messageId,
            chat_id: response.chatId,
            role: 'assistant',
            content: response.response,
            metadata: response.metadata,
            context_sources: response.metadata?.sourcesUsed ?? null,
            created_by: 'system',
            created_at: new Date().toISOString(),
          };

          // Seed the messages cache so there's no empty flash
          const userMsg: ChatMessage = {
            ...optimisticUserMessage,
            chat_id: response.chatId,
            id: `user-${response.chatId}`, // stable ID until server refetch
          };
          queryClient.setQueryData(
            queryKeys.chat.messages(response.chatId),
            [userMsg, assistantMessage],
          );

          setActiveChatId(response.chatId);
          setPendingFirstMessage(null);
        }

        return response;
      } catch (error) {
        // Remove the optimistic message on error
        if (activeChatId) {
          queryClient.setQueryData(
            queryKeys.chat.messages(activeChatId),
            (old: ChatMessage[] | undefined) => {
              return old ? old.filter((msg) => msg.id !== tempMessageId) : [];
            },
          );
        } else {
          setPendingFirstMessage(null);
        }
        throw error;
      }
    },
    [activeChatId, clientId, effectiveScope, sendMessageMutation, queryClient],
  );

  /**
   * Start a new chat (clears active chat so next message creates new)
   */
  const startNewChat = useCallback(() => {
    setActiveChatId(null);
    setPendingFirstMessage(null);
  }, []);

  /**
   * Select an existing chat
   */
  const selectChat = useCallback((chatId: string) => {
    setActiveChatId(chatId);
    setPendingFirstMessage(null);
  }, []);

  // Merge server messages with pending first message for display
  const serverMessages = messages.data ?? [];
  const allMessages = pendingFirstMessage
    ? [...serverMessages, pendingFirstMessage]
    : serverMessages;

  return {
    // Chat list
    chats: chats.data ?? [],
    chatsLoading: chats.isLoading,
    chatsError: chats.error,

    // Current chat messages
    messages: allMessages,
    messagesLoading: messages.isLoading,
    messagesError: messages.error,

    // Active chat state
    activeChatId,
    setActiveChatId,

    // Actions
    sendMessage: handleSendMessage,
    startNewChat,
    selectChat,

    // Status
    isLoading: chats.isLoading || messages.isLoading,
    isSending: sendMessageMutation.isPending,
    sendError: sendMessageMutation.error,

    // Refetch functions
    refetchChats: chats.refetch,
    refetchMessages: messages.refetch,
  };
}
